import { useCallback, useMemo, useState } from 'react';
import {
  getProfilerHeatmapCollapsedByProjectView,
  getProfilerHeatmapColorCodingSelectionByProjectView,
  getProfilerHeatmapGroupingToggleByProjectView,
  getProfilerHeatmapTileSizeSelectionByProjectView,
  getProfilerSortSelectionByProjectView,
  getProfilerViewSelectionByProjectView,
  saveProfilerHeatmapCollapsedByProjectView,
  saveProfilerHeatmapColorCodingSelectionByProjectView,
  saveProfilerHeatmapGroupingToggleByProjectView,
  saveProfilerHeatmapTileSizeSelectionByProjectView,
  saveProfilerSortSelectionByProjectView,
  saveProfilerViewSelectionByProjectView
} from '../pages/ProfilerProjects/profilerProjectViewConstants';
import { UI_OPTIONS_UTILS } from '../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../utils/constants/uiOptionKeys';
import profilerSearchSortOptions from '../pages/search/SearchResults/constants/profilerSearchSortOptions';
import {
  coerceHeatmapColorCodingForLite,
  coerceHeatmapTileSizeForLite,
  filterEbitSortOptions,
  filterHeatmapColorCodingOptionsForAccess,
  filterHeatmapTileSizeOptionsForAccess
} from '../pages/search/SearchResults/constants/constants';

/**
 * Owns view-picker / sort / heatmap selections + their menu-toggle UI state.
 * Persistence is keyed by `searchId` and shared with Projects View, so a
 * selection set in one page surfaces in the other.
 *
 * `hasEbitData` says whether the caller's result set carries EBIT margins; it defaults to
 * true (non-gating). Keyed on the data rather than on a tier so this hook needs no access
 * store and no async owner lookup — see `hasEbitMarginData` (TD-2890).
 */
export default function useProfilerViewControls({
  searchId,
  searchCategory,
  hasEbitData = true
} = {}) {
  const [profilerView, setProfilerViewState] = useState(() =>
    getProfilerViewSelectionByProjectView(searchId)
  );
  const [selectedSort, setSelectedSortState] = useState(() =>
    getProfilerSortSelectionByProjectView(searchId)
  );
  const [profilerHeatmapTileSize, setProfilerHeatmapTileSizeState] = useState(() =>
    getProfilerHeatmapTileSizeSelectionByProjectView(searchId)
  );
  const [profilerHeatmapColorCoding, setProfilerHeatmapColorCodingState] = useState(() =>
    getProfilerHeatmapColorCodingSelectionByProjectView(searchId)
  );
  const [isProfilerHeatmapGroupingToggled, setIsProfilerHeatmapGroupingToggled] = useState(() =>
    getProfilerHeatmapGroupingToggleByProjectView(searchId)
  );
  // TD-2733 — the stacked heatmap folds away for a full-height list. Sixth member of the
  // same persisted set as the view, sort and the three heatmap metrics above.
  const [isProfilerHeatmapCollapsed, setIsProfilerHeatmapCollapsed] = useState(() =>
    getProfilerHeatmapCollapsedByProjectView(searchId)
  );

  const [isSortToggled, setIsSortToggled] = useState(false);
  const [isProfilerViewMenuToggled, setIsProfilerViewMenuToggled] = useState(false);
  const [isProfilerHeatmapTileSizeMenuToggled, setIsProfilerHeatmapTileSizeMenuToggled] =
    useState(false);
  const [isProfilerHeatmapColorCodingMenuToggled, setIsProfilerHeatmapColorCodingMenuToggled] =
    useState(false);

  const setProfilerView = useCallback(
    (value) => {
      setProfilerViewState(value);
      saveProfilerViewSelectionByProjectView(value, searchId);
    },
    [searchId]
  );

  const selectSortOption = useCallback(
    (value) => {
      setSelectedSortState(value);
      saveProfilerSortSelectionByProjectView(value, searchId);
    },
    [searchId]
  );

  const setProfilerHeatmapTileSize = useCallback(
    (value) => {
      setProfilerHeatmapTileSizeState(value);
      saveProfilerHeatmapTileSizeSelectionByProjectView(value, searchId);
    },
    [searchId]
  );

  const setProfilerHeatmapColorCoding = useCallback(
    (value) => {
      setProfilerHeatmapColorCodingState(value);
      saveProfilerHeatmapColorCodingSelectionByProjectView(value, searchId);
    },
    [searchId]
  );

  const toggleProfilerHeatmapGrouping = useCallback(() => {
    setIsProfilerHeatmapGroupingToggled((prev) => {
      const next = !prev;
      saveProfilerHeatmapGroupingToggleByProjectView(next, searchId);
      return next;
    });
  }, [searchId]);

  const toggleProfilerHeatmapCollapsed = useCallback(() => {
    setIsProfilerHeatmapCollapsed((prev) => {
      const next = !prev;
      saveProfilerHeatmapCollapsedByProjectView(next, searchId);
      return next;
    });
  }, [searchId]);

  const toggleSort = useCallback(() => setIsSortToggled((prev) => !prev), []);
  const toggleProfilerViewMenu = useCallback(
    () => setIsProfilerViewMenuToggled((prev) => !prev),
    []
  );
  const toggleProfilerHeatmapTileSizeMenu = useCallback(
    () => setIsProfilerHeatmapTileSizeMenuToggled((prev) => !prev),
    []
  );
  const toggleProfilerHeatmapColorCodingMenu = useCallback(
    () => setIsProfilerHeatmapColorCodingMenuToggled((prev) => !prev),
    []
  );

  const profilerSortOptions = useMemo(() => {
    const options = searchCategory
      ? profilerSearchSortOptions.filter((o) =>
          o.onlyForSearchCategory ? o.onlyForSearchCategory === searchCategory : true
        )
      : profilerSearchSortOptions;

    // TD-2890 — this view has no columns to filter sorts against, so the EBIT-margin
    // options showed at every width. The result set is the gate.
    return filterEbitSortOptions(options, hasEbitData);
  }, [searchCategory, hasEbitData]);

  // TD-2890 — same gate as the sort options. Without it this surface showed a picker
  // reading "Color coding: EBIT Margin (%)" (the persistence helper falls back to EBIT
  // when nothing is stored) over a heatmap painted entirely NEUTRAL grey, because the
  // BE served no margins. Search and Projects already do this via the TD-2499 helpers.
  const profilerHeatmapTileSizeOptions = filterHeatmapTileSizeOptionsForAccess(
    UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE],
    hasEbitData
  );
  const profilerHeatmapColorCodingOptions = filterHeatmapColorCodingOptionsForAccess(
    UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING],
    hasEbitData
  );

  return {
    profilerView,
    setProfilerView,
    selectedSort,
    selectSortOption,
    // Coerced on the way out, never in storage: results arrive async, so rewriting the
    // stored metric would be a one-shot guess. The setters keep writing the raw choice.
    profilerHeatmapTileSize: coerceHeatmapTileSizeForLite(profilerHeatmapTileSize, !hasEbitData),
    setProfilerHeatmapTileSize,
    profilerHeatmapColorCoding: coerceHeatmapColorCodingForLite(
      profilerHeatmapColorCoding,
      !hasEbitData
    ),
    setProfilerHeatmapColorCoding,
    isProfilerHeatmapGroupingToggled,
    toggleProfilerHeatmapGrouping,
    isProfilerHeatmapCollapsed,
    toggleProfilerHeatmapCollapsed,
    isSortToggled,
    toggleSort,
    isProfilerViewMenuToggled,
    toggleProfilerViewMenu,
    isProfilerHeatmapTileSizeMenuToggled,
    toggleProfilerHeatmapTileSizeMenu,
    isProfilerHeatmapColorCodingMenuToggled,
    toggleProfilerHeatmapColorCodingMenu,
    profilerSortOptions,
    profilerHeatmapTileSizeOptions,
    profilerHeatmapColorCodingOptions
  };
}
